import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { PageContainer, PageHeader } from "../components/PageHeader";
import {
  EngagementBreakdown,
  EngagementMetric,
  formatDuration,
  formatPercent,
} from "../components/engagementFormat";
import { foyerApi, type CompanySummary } from "../http/foyer";
import type { ChainEngagementSummary } from "@shared/types/engagement";

/**
 * Chain-level content analytics: every property under one company, side by side.
 *
 * The premise panel answers "is my content working?"; this answers the question
 * only a group can ask - "which of my properties is getting value out of this,
 * and what are the good ones doing differently?". That comparison is the reason
 * a chain buys analytics centrally rather than leaving each hotel to its own
 * dashboard, so the table leads with the busiest property and carries each
 * one's best promotion and most-launched app beside its totals.
 *
 * The company selector shows every company the session can see; the report
 * itself only ever contains properties the session may access, so a regional
 * manager scoped to four hotels asking for the parent group gets a four-hotel
 * report rather than a refusal.
 */

const RANGES = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 365, label: "Last 12 months" },
];

export default function ContentAnalyticsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [companies, setCompanies] = useState<CompanySummary[] | null>(null);
  const [data, setData] = useState<ChainEngagementSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const companyId = params.get("company") ?? "";
  const days = Number(params.get("days")) || 30;

  const setParam = useCallback(
    (key: string, value: string) => {
      // Kept in the URL so a chain report is a link somebody can paste into an
      // email - the single most common thing anyone does with one of these.
      const next = new URLSearchParams(params);
      next.set(key, value);
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  useEffect(() => {
    void (async () => {
      try {
        const list = await foyerApi.listCompanies();
        setCompanies(list);
        if (!companyId && list.length) setParam("company", list[0].id);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load");
      }
    })();
    // Only on mount: re-running this on every `setParam` identity change would
    // fight the user's own company selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const summary = await foyerApi.chainEngagement(companyId, days);
        if (!cancelled) {
          setData(summary);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, days]);

  const retentionNote = useMemo(() => {
    if (!data) return null;
    const years = data.retentionDays / 365.25;
    return years >= 1
      ? `${years.toFixed(1)} years (${data.retentionDays} days)`
      : `${data.retentionDays} days`;
  }, [data]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Insights"
        title="Content analytics"
        actions={
          <>
            <TextField
              select
              size="small"
              label="Company"
              value={companies ? companyId : ""}
              onChange={(e) => setParam("company", e.target.value)}
              data-testid="chain-company"
              sx={{ minWidth: 220 }}
              disabled={!companies?.length}
            >
              {(companies ?? []).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Range"
              value={days}
              onChange={(e) => setParam("days", e.target.value)}
              data-testid="chain-range"
              sx={{ minWidth: 170 }}
            >
              {RANGES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              component="a"
              href={
                companyId
                  ? foyerApi.chainEngagementCsvUrl(companyId, days)
                  : "#"
              }
              disabled={!companyId}
              startIcon={<DownloadOutlinedIcon />}
              variant="outlined"
              data-testid="chain-export"
            >
              Export CSV
            </Button>
          </>
        }
      />

      {error && <Alert severity="error">{error}</Alert>}
      {loading && !data && <CircularProgress size={24} />}
      {companies?.length === 0 && (
        <Alert severity="info">
          No companies yet. Create one under Companies to group properties into
          a chain.
        </Alert>
      )}

      {data && (
        <Stack spacing={3}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={5} flexWrap="wrap" useFlexGap>
                <EngagementMetric
                  label="Properties"
                  value={data.premiseCount}
                />
                <EngagementMetric
                  label="Section views"
                  value={data.totals.sectionViews}
                />
                <EngagementMetric
                  label="App launches"
                  value={data.totals.appLaunches}
                  hint={`${formatDuration(data.totals.appSeconds)} watched`}
                />
                <EngagementMetric
                  label="Screen shares"
                  value={data.totals.castStarts}
                  hint={`${formatDuration(data.totals.castSeconds)} of casting`}
                />
                <EngagementMetric
                  label="Promo impressions"
                  value={data.totals.slotViews}
                  hint={`${data.totals.slotEngagedViews.toLocaleString()} engaged`}
                />
                <EngagementMetric
                  label="Information read"
                  value={data.totals.infoReads}
                />
              </Stack>

              {data.premiseCount === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No properties in this company are visible to your account.
                </Alert>
              )}

              <Divider sx={{ my: 3 }} />

              <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                <EngagementBreakdown
                  title="Streaming apps across the chain"
                  entries={data.apps}
                  empty="No apps launched yet."
                />
                <EngagementBreakdown
                  title="Languages guests chose"
                  entries={data.languages}
                  empty="Nobody switched language."
                />
                <EngagementBreakdown
                  title="Best-performing promotions"
                  entries={data.slots}
                  empty="No promotions were shown."
                />
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Property comparison
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Busiest first. Click a property to open its own engagement
                report.
              </Typography>
              <TableContainer>
                <Table size="small" data-testid="chain-premises">
                  <TableHead>
                    <TableRow>
                      <TableCell>Property</TableCell>
                      <TableCell align="right">Section views</TableCell>
                      <TableCell align="right">App launches</TableCell>
                      <TableCell align="right">Time in apps</TableCell>
                      <TableCell align="right">Screen shares</TableCell>
                      <TableCell align="right">Promo impressions</TableCell>
                      <TableCell align="right">Engaged</TableCell>
                      <TableCell>Top app</TableCell>
                      <TableCell>Top promotion</TableCell>
                      <TableCell>Top language</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.premises.map((p) => (
                      <TableRow
                        key={p.premiseId}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => navigate(`/premises/${p.premiseId}`)}
                      >
                        <TableCell>{p.premiseName}</TableCell>
                        <TableCell align="right">
                          {p.totals.sectionViews.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          {p.totals.appLaunches.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          {formatDuration(p.totals.appSeconds)}
                        </TableCell>
                        <TableCell align="right">
                          {p.totals.castStarts.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          {p.totals.slotViews.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          {p.totals.slotViews
                            ? formatPercent(
                                p.totals.slotEngagedViews / p.totals.slotViews
                              )
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <TopCell
                            label={p.topApp?.label}
                            detail={
                              p.topApp
                                ? `${p.topApp.count.toLocaleString()} launches`
                                : undefined
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TopCell
                            label={p.topSlot?.label}
                            detail={
                              p.topSlot
                                ? `${formatPercent(p.topSlot.engagedRate)} engaged`
                                : undefined
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TopCell label={p.topLanguage?.label} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {data.premises.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Nothing to compare yet.
                </Typography>
              )}
            </CardContent>
          </Card>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Every figure here is a sum of per-property, per-day counts
              recorded on the televisions themselves and attributed by their
              screen tokens. No guest, room or session is identified at any
              point, so no chain-level view can reconstruct one. Rollups are
              kept for {retentionNote} and are then deleted automatically.
            </Typography>
          </Box>
        </Stack>
      )}
    </PageContainer>
  );
}

/** A "best X" cell that degrades to an em dash rather than an empty box. */
function TopCell({ label, detail }: { label?: string; detail?: string }) {
  if (!label) {
    return (
      <Typography variant="body2" color="text.disabled">
        -
      </Typography>
    );
  }
  return (
    <Tooltip title={detail ?? ""}>
      <Box>
        <Typography variant="body2" noWrap>
          {label}
        </Typography>
        {detail && (
          <Typography variant="caption" color="text.secondary">
            {detail}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}
